import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../../components/Input';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import Spinner from '../../../../components/Spinner';
import InternalUserService, { InternalUserFormPayload } from '../../../../services/internal-user.service';
import RoleMasterService from '../../../../services/role-master.service';
import ProductService from '../../../../services/product.service';
import { AuthContext } from '../../../../contexts/AuthContext';
import { Modules, AuthRoutes } from '../../../../enums';

interface RoleOption {
  id: number;
  code: string;
  name: string;
  scope: 'global' | 'product';
}

interface ProductOption {
  id: number;
  product_code: string;
  product_name: string;
}

interface InternalUserFormState {
  full_name: string;
  email: string;
  phone: string;
  role_id: number | '';
  product_scope: number[];
  notes: string;
  status: string;
}

const initialForm: InternalUserFormState = {
  full_name: '',
  email: '',
  phone: '',
  role_id: '',
  product_scope: [],
  notes: '',
  status: 'invited',
};

const InternalUserForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = !!id;
  const { loggedInUser } = useContext(AuthContext);
  const isEditingSelf = edit && Number(id) === Number(loggedInUser?.id);

  const [formData, setFormData] = useState<InternalUserFormState>(initialForm);
  const [originalRoleId, setOriginalRoleId] = useState<number | ''>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);

  useEffect(() => {
    RoleMasterService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setRoles(response?.data?.items ?? []);
    });
    ProductService.list({ includeDeleted: false, limit: 100 }).then((response: any) => {
      setProducts(response?.data?.items ?? []);
    });
  }, []);

  useEffect(() => {
    if (!id) {
      setFormData(initialForm);
      return;
    }
    setFetching(true);
    InternalUserService.get(id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          full_name: row.full_name ?? '',
          email: row.email ?? '',
          phone: row.phone ?? '',
          role_id: row.role_id ?? '',
          product_scope: row.product_scope ?? [],
          notes: row.notes ?? '',
          status: row.status ?? 'invited',
        });
        setOriginalRoleId(row.role_id ?? '');
      })
      .finally(() => setFetching(false));
  }, [id]);

  const selectedRole = useMemo(() => roles.find((r) => r.id === formData.role_id), [roles, formData.role_id]);
  const isProductScoped = selectedRole?.scope === 'product';
  const roleChanged = edit && formData.role_id !== '' && formData.role_id !== originalRoleId;

  const validate = useCallback(
    (data: InternalUserFormState) => {
      const nextErrors: Record<string, string> = {};
      if (!data.full_name.trim()) nextErrors.full_name = t('iam.internalUsers.form.pageTexts.fields.fullName.errors.required');
      if (!data.email.trim()) nextErrors.email = t('iam.internalUsers.form.pageTexts.fields.email.errors.required');
      if (!data.role_id) nextErrors.role_id = t('iam.internalUsers.form.pageTexts.fields.role.errors.required');
      if (selectedRole?.scope === 'product' && data.product_scope.length === 0) {
        nextErrors.product_scope = t('iam.internalUsers.form.pageTexts.fields.productScope.errors.required');
      }
      return nextErrors;
    },
    [t, selectedRole]
  );

  const handleChange = useCallback((e: any, name: string) => {
    const value = name === 'role_id' ? Number(e.target.value) : e.target.value;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'role_id') {
        const nextRole = roles.find((r) => r.id === Number(e.target.value));
        if (nextRole?.scope === 'global') next.product_scope = [];
      }
      return next;
    });
  }, [roles]);

  const toggleProduct = useCallback((productId: number) => {
    setFormData((prev) => {
      const has = prev.product_scope.includes(productId);
      return { ...prev, product_scope: has ? prev.product_scope.filter((pid) => pid !== productId) : [...prev.product_scope, productId] };
    });
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload: InternalUserFormPayload = {
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone || undefined,
          role_id: Number(formData.role_id),
          product_scope: isProductScoped ? formData.product_scope : [],
          notes: formData.notes || undefined,
        };
        if (edit && id) {
          await InternalUserService.update(id, payload);
          toast.success(<Toast message={t('iam.internalUsers.form.pageTexts.updateSuccess')} />, { icon: false });
        } else {
          await InternalUserService.create(payload);
          toast.success(<Toast message={t('iam.internalUsers.form.pageTexts.inviteSuccess', { email: formData.email })} />, { icon: false });
        }
        navigate(`${Modules.iam}${AuthRoutes.internalUsers}`);
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    },
    [formData, isProductScoped, edit, id, navigate, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit ? t('iam.internalUsers.form.pageTexts.editTitle') : t('iam.internalUsers.form.pageTexts.addTitle')}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-4">
          <Input
            id="full_name"
            name="full_name"
            label={t('iam.internalUsers.form.pageTexts.fields.fullName.label')}
            placeholder={t('iam.internalUsers.form.pageTexts.fields.fullName.placeholder')}
            required
            value={formData.full_name}
            error={!!errors.full_name}
            errorMessage={errors.full_name}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="email"
            name="email"
            type="email"
            label={t('iam.internalUsers.form.pageTexts.fields.email.label')}
            placeholder={t('iam.internalUsers.form.pageTexts.fields.email.placeholder')}
            required
            value={formData.email}
            error={!!errors.email}
            errorMessage={errors.email}
            onChange={handleChange}
          />
          {edit && (
            <div className="form-text">{t('iam.internalUsers.form.pageTexts.fields.email.editHint')}</div>
          )}
        </div>
        <div className="col-md-4">
          <Input
            id="phone"
            name="phone"
            label={t('iam.internalUsers.form.pageTexts.fields.phone.label')}
            placeholder={t('iam.internalUsers.form.pageTexts.fields.phone.placeholder')}
            value={formData.phone}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label d-block" htmlFor="role_id">
            {t('iam.internalUsers.form.pageTexts.fields.role.label')}
          </label>
          <select
            id="role_id"
            name="role_id"
            className={`form-select ${errors.role_id ? 'is-invalid' : ''}`}
            value={formData.role_id}
            disabled={isEditingSelf}
            onChange={(e) => handleChange(e, 'role_id')}
          >
            <option value="">{t('iam.internalUsers.form.pageTexts.fields.role.placeholder')}</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name} ({role.scope})
              </option>
            ))}
          </select>
          {isEditingSelf && (
            <div className="form-text">{t('iam.internalUsers.form.pageTexts.fields.role.selfEditHint')}</div>
          )}
          {!isEditingSelf && roleChanged && (
            <div className="form-text text-warning">{t('iam.internalUsers.form.pageTexts.fields.role.changeWarning')}</div>
          )}
        </div>
        <div className="col-md-4">
          <label className="form-label d-block">{t('iam.internalUsers.form.pageTexts.fields.mfa.label')}</label>
          <span className="badge bg-label-info">{t('iam.internalUsers.form.pageTexts.fields.mfa.value')}</span>
        </div>
        <div className="col-md-4">
          <label className="form-label d-block">{t('iam.internalUsers.form.pageTexts.fields.status.label')}</label>
          <span className="badge bg-label-warning">
            {edit ? formData.status : t('iam.internalUsers.form.pageTexts.fields.status.value')}
          </span>
        </div>

        {isProductScoped && (
          <div className="col-12">
            <label className="form-label d-block">{t('iam.internalUsers.form.pageTexts.fields.productScope.label')}</label>
            <div className="d-flex flex-wrap gap-3">
              {products.map((product) => (
                <div className="form-check" key={product.id}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`product-${product.id}`}
                    checked={formData.product_scope.includes(product.id)}
                    onChange={() => toggleProduct(product.id)}
                  />
                  <label className="form-check-label" htmlFor={`product-${product.id}`}>
                    {product.product_code}
                  </label>
                </div>
              ))}
            </div>
            {errors.product_scope && <div className="text-danger mt-1">{errors.product_scope}</div>}
          </div>
        )}

        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('iam.internalUsers.form.pageTexts.fields.notes.label')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>

        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('iam.internalUsers.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.iam}${AuthRoutes.internalUsers}`)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={edit ? t('iam.internalUsers.form.pageTexts.buttons.save') : t('iam.internalUsers.form.pageTexts.buttons.invite')}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default InternalUserForm;
